import type { ViewerInstance } from '../types';

const COOLDOWN_KEY = 'storysplat.webgpuRecoveryUntil';
const COOLDOWN_MS = 10 * 60 * 1000;
let memoryUntil = 0;

/** Temporary browser preference; never written into a saved scene. */
export function preferWebGLAfterFailure(): boolean {
    let until = memoryUntil;
    try { until = Math.max(until, Number(localStorage.getItem(COOLDOWN_KEY)) || 0); } catch { /* private/embedded browsing */ }
    return until > Date.now();
}

export function rememberGraphicsFailure(): void {
    memoryUntil = Date.now() + COOLDOWN_MS;
    try { localStorage.setItem(COOLDOWN_KEY, String(memoryUntil)); } catch { /* memory fallback */ }
}

/** Propagate a worker's CPU fallback to subsequent viewers and conversions. */
export function trackGraphicsWorker<T extends Worker>(worker: T): T {
    worker.addEventListener('message', (event: MessageEvent) => {
        if (event.data?.type === 'progress' && event.data?.gpuFallback === true) rememberGraphicsFailure();
    });
    return worker;
}

export type GraphicsRecoveryRequest = (reason: string) => boolean | Promise<boolean>;

type ViewerEvent = Parameters<ViewerInstance['on']>[0];
type Callback = Parameters<ViewerInstance['on']>[1];
type RecoveryDevice = ViewerInstance['app']['graphicsDevice'] & {
    wgpu?: EventTarget & { lost: Promise<{ reason: string; message: string }> };
};

/** A stable SDK handle while the renderer is replaced. Editor owners rebuild their
 * canvas tools using onRecover, retaining the parent React scene and file state. */
export async function createRecoverableViewer(
    container: HTMLElement,
    create: (webgl: boolean) => Promise<ViewerInstance>,
    onRecover?: GraphicsRecoveryRequest,
): Promise<ViewerInstance> {
    let current: ViewerInstance;
    let disposed = false;
    const destroyedInstances = new WeakSet<ViewerInstance>();
    let busy = false;
    let automaticAttempted = preferWebGLAfterFailure();
    let cleanupMonitor = () => {};
    let panel: HTMLDivElement | undefined;
    const listeners = new Map<ViewerEvent, Map<Callback, boolean>>();
    function clearPanel() { panel?.remove(); panel = undefined; }
    function showPanel(message: string, retry?: () => void) {
        clearPanel();
        panel = document.createElement('div');
        panel.className = 'storysplat-graphics-recovery';
        panel.setAttribute('role', 'alert');
        panel.style.cssText = 'position:absolute;inset:20% 8% auto;z-index:100000;padding:24px;border-radius:12px;background:#161b23;color:white;font:16px/1.5 system-ui;text-align:center;box-shadow:0 8px 40px #0008;pointer-events:auto';
        const text = document.createElement('p');
        text.textContent = message;
        panel.append(text);
        if (retry) {
            const button = document.createElement('button');
            button.textContent = 'Retry preview';
            button.style.cssText = 'padding:10px 20px;border:0;border-radius:6px;background:#fff;color:#161b23;font:inherit;cursor:pointer';
            button.onclick = retry;
            panel.append(button);
        }
        // Keep initialization retry reachable until a viewer has been created.
        if (current) {
            const dismiss = document.createElement('button');
            dismiss.textContent = 'Close';
            dismiss.style.cssText = 'display:block;margin:12px auto 0;border:0;background:none;color:#ccc;text-decoration:underline;cursor:pointer;font:inherit';
            dismiss.onclick = clearPanel;
            panel.append(dismiss);
        }
        container.append(panel);
    }

    function destroyCurrent() {
        cleanupMonitor();
        if (!current || destroyedInstances.has(current)) return;
        destroyedInstances.add(current);
        try { current.destroy(); } catch (error) {
            console.warn('[StorySplat] Renderer cleanup after graphics failure:', error);
            // Engine destruction can itself encounter the lost device. Stop its
            // frame loop even if resource cleanup cannot finish.
            try { current?.app?.destroy(); } catch { /* already lost */ }
            current?.canvas?.remove();
        }
    }

    async function build(webgl: boolean): Promise<ViewerInstance> {
        const previousNodes = new Set(container.childNodes);
        try {
            const next = await create(webgl);
            if (disposed) { next.destroy(); throw new Error('Viewer destroyed during recovery'); }
            return next;
        } catch (error) {
            for (const node of Array.from(container.childNodes)) {
                if (!previousNodes.has(node) && node !== panel) node.remove();
            }
            throw error;
        }
    }

    async function recover(reason: string, automatic = false): Promise<void> {
        if (disposed || busy) return;
        if (automatic && automaticAttempted) {
            cleanupMonitor();
            showPanel('The graphics preview stopped. You can retry when the browser’s graphics service is available.', () => { void recover(reason); });
            return;
        }
        automaticAttempted = true;
        busy = true;
        rememberGraphicsFailure();
        cleanupMonitor();
        showPanel('Restarting the graphics preview in compatibility mode…');
        try {
            if (onRecover) {
                const accepted = await onRecover(reason);
                if (!accepted && !disposed) showPanel('The preview stopped. Save or export your active splat edits, then retry. Your editor has stayed open.', () => { void recover(reason); });
                return;
            }
            const snapshot = current && {
                mode: current.getCameraMode(), progress: current.getProgress(),
                position: current.getPosition(), rotation: current.getRotation(),
                muted: current.isMuted(),
            };
            destroyCurrent();
            current = await build(true);
            for (const [event, callbacks] of listeners) {
                for (const [callback, internal] of callbacks) {
                    (internal ? current.onInternal ?? current.on : current.on)(event, callback);
                }
            }
            if (snapshot) {
                const restoredViewer = current;
                const restore = () => {
                    restoredViewer.off('loaded', restore);
                    try {
                        if (snapshot.mode === 'tour') {
                            restoredViewer.setCameraMode('tour');
                            restoredViewer.pause();
                            restoredViewer.setProgress(snapshot.progress, false);
                        } else {
                            // Pose setters require explore mode. A loaded-event
                            // consumer may have changed the mode before this observer.
                            restoredViewer.setCameraMode('explore');
                            restoredViewer.setPosition(snapshot.position.x, snapshot.position.y, snapshot.position.z);
                            restoredViewer.setRotation(snapshot.rotation.x, snapshot.rotation.y, snapshot.rotation.z);
                            if (snapshot.mode === 'walk') restoredViewer.setCameraMode('walk');
                        }
                        if (snapshot.muted) restoredViewer.muteAll();
                        else restoredViewer.unmuteAll();
                    } catch (error) {
                        // An optional pose restoration must not turn a successfully
                        // loaded scene into an asset-load failure.
                        console.warn('[StorySplat] Could not restore the previous camera:', error);
                    }
                };
                current.onInternal?.('loaded', restore);
            }
            clearPanel();
            monitor();
        } catch (error) {
            console.error('[StorySplat] Graphics recovery failed:', error);
            if (!disposed) showPanel('The browser could not restart the preview. Try again shortly, or open this scene in another browser. The page has stayed open.', () => { void recover(reason); });
        } finally { busy = false; }
    }

    function monitor() {
        const target = current;
        // Lazy viewers install their own monitor when Start creates the renderer.
        if (!target.app || !target.canvas) return;
        const device = target.app.graphicsDevice as RecoveryDevice;
        let stopped = false;
        let lastFrame = performance.now();
        let visible = true;
        const observer = typeof IntersectionObserver === 'undefined' ? undefined : new IntersectionObserver(entries => {
            visible = entries.some(entry => entry.isIntersecting);
            lastFrame = performance.now();
        });
        observer?.observe(target.canvas);
        const frame = () => { lastFrame = performance.now(); };
        const fail = (reason: string) => { if (!stopped && !disposed) void recover(reason, true); };
        const lost = () => fail('Graphics device lost');
        const webglLost = (event: Event) => { event.preventDefault(); lost(); };
        const gpuError = (event: Event) => fail((event as Event & { error: Error }).error.message);
        const loadError = (error: Error) => {
            if (!stopped) showPanel(error?.message || 'The scene could not be displayed.', () => { void recover('Preview load failed'); });
        };
        device.wgpu?.lost.then(info => {
            if (info.reason !== 'destroyed') fail(`WebGPU device lost: ${info.message || info.reason}`);
        }).catch(error => fail(String(error)));
        device.wgpu?.addEventListener('uncapturederror', gpuError);
        device.on('devicelost', lost);
        target.canvas.addEventListener('webglcontextlost', webglLost);
        target.app.on('frameend', frame);
        target.onInternal?.('error', loadError);
        // Detect an interrupted frame loop, not canvas colours: empty/gray scenes
        // can be intentional, and pixel readback can itself stress the GPU.
        const timer = setInterval(() => {
            if (!visible || document.hidden || !target.canvas.isConnected || !target.canvas.clientWidth || !target.canvas.clientHeight) {
                lastFrame = performance.now();
                return;
            }
            if (performance.now() - lastFrame > 15000) fail('Rendering stopped responding');
            else target.app.renderNextFrame = true;
        }, 5000);
        cleanupMonitor = () => {
            if (stopped) return;
            stopped = true;
            clearInterval(timer);
            target.app.off('frameend', frame);
            observer?.disconnect();
            target.app.off('destroy', ownerDestroyed);
            device.off('devicelost', lost);
            device.wgpu?.removeEventListener('uncapturederror', gpuError);
            target.canvas.removeEventListener('webglcontextlost', webglLost);
            target.off('error', loadError);
        };
        // Portal navigation and owner teardown must cancel the old watchdog too.
        const ownerDestroyed = () => {
            cleanupMonitor();
            if (!busy) { disposed = true; clearPanel(); }
        };
        target.app.once('destroy', ownerDestroyed);
    }

    try { current = await build(preferWebGLAfterFailure()); }
    catch (error) {
        rememberGraphicsFailure();
        if (onRecover) {
            showPanel('The browser could not start the graphics preview. Your editor is still open.', () => { void recover('Graphics initialization failed'); });
            throw error;
        }
        // Keep the initial SDK promise pending while the user retries, so its
        // caller receives the working instance and can attach its normal events.
        current = await new Promise<ViewerInstance>(resolve => {
            const retry = async () => {
                if (busy) return;
                busy = true;
                try { resolve(await build(true)); clearPanel(); }
                catch { showPanel('Graphics are still unavailable. Try again shortly or use another browser.', () => { void retry(); }); }
                finally { busy = false; }
            };
            showPanel('The browser could not start the graphics preview. Try again shortly or use another browser.', () => { void retry(); });
        });
    }
    monitor();
    const methods = new Map<PropertyKey, Callback>();
    return new Proxy(current, {
        get(_target, key) {
            if (key === 'destroy') return () => { disposed = true; clearPanel(); destroyCurrent(); };
            if (key === 'on' || key === 'onInternal') return (event: ViewerEvent, callback: Callback) => {
                if (!listeners.has(event)) listeners.set(event, new Map());
                listeners.get(event)!.set(callback, key === 'onInternal');
                (key === 'onInternal' ? current.onInternal ?? current.on : current.on)(event, callback);
            };
            if (key === 'off') return (event: ViewerEvent, callback: Callback) => {
                listeners.get(event)?.delete(callback);
                current.off(event, callback);
            };
            const value = Reflect.get(current, key);
            if (typeof value !== 'function') return value;
            if (!methods.has(key)) methods.set(key, (...args: Parameters<Callback>) => Reflect.get(current, key).apply(current, args));
            return methods.get(key);
        },
        set(_target, key, value) { return Reflect.set(current, key, value); },
    });
}
